<script>
import _throttle from 'lodash/throttle'

const breakpoints = {
  // From bootstrap - https://getbootstrap.com/docs/4.3/layout/grid/#sass-mixins
  xs: 0,
  sm: 360,
  md: 720,
  lg: 1024,
  xl: 1300
}

/**
 * Contient le contexte général d'exécution permettant de basculer le comportement de composants.
 *
 * Propriétés :
 *
 * - `screenType`: Taille de l'écran sous forme de label (xs, sm, md, lg, xl)
 * - `hasTouch`: Indique si l'écran dispose d'une fonction "tactile"
 *
 * Méthodes :
 *
 * - `up`: Retourne vrai si la taille d'écran est supérieure ou égale à celle passée en paramètre
 * - `down`: Retourne vrai si la taille d'écran est inférieure ou égale à celle passée en paramètre
 */
export default {
  name: 'UbiBrowserContext',
  data () {
    return {
      browserContext: {
        /**
         * Taille de l'écran sous forme de label (xs, sm, md, lg, xl)
         */
        screenType: null,
        /**
         * Indique si l'écran dispose d'une fonction "tactile"
         */
        hasTouch: false,
        /**
         * Retourne vrai si la taille d'écran est supérieure ou égale à celle passée en paramètre
         */
        up (breakpoint) {
          return breakpoints[breakpoint] <= breakpoints[this.screenType]
        },
        /**
         * Retourne vrai si la taille d'écran est inférieure ou égale à celle passée en paramètre
         */
        down (breakpoint) {
          return breakpoints[breakpoint] >= breakpoints[this.screenType]
        }
      }
    }
  },
  provide () {
    return {
      browserContext: this.browserContext
    }
  },
  created () {
    if (!window) return
    this.evaluateScreenType()
    this.evaluateHasTouch()
  },
  mounted () {
    if (!window) return
    window.onresize = _throttle(this.evaluateScreenType, 250)
  },
  beforeDestroy () {
    if (!window) return
    window.onresize = null
  },
  render: function () {
    return this.$slots.default
  },
  methods: {
    evaluateScreenType () {
      let screenType = 'xs'
      for (const bp in breakpoints) {
        if (breakpoints[bp] > window.innerWidth) break
        screenType = bp
      }
      this.$set(this.browserContext, 'screenType', screenType)
    },
    // From https://github.com/Modernizr/Modernizr/blob/master/feature-detects/touchevents.js
    evaluateHasTouch () {
      this.$set(
        this.browserContext,
        'hasTouch',
        'ontouchstart' in window ||
          (window.DocumentTouch && document instanceof window.DocumentTouch) ||
          window.matchMedia(
            [
              '(',
              ['', '-webkit-', '-moz-', '-o-', '-ms-'].join('touch-enabled),('),
              'heartz',
              ')'
            ].join('')
          ).matches
      )
    }
  }
}
</script>

<docs>
En ajoutant `inject: ["browserContext"]` à la définition d'un composant, il a accès à la variable `this.browserContext` qui contient les informations sur le contexte de navigation.

### Exemple

```javascript
{
  inject: ["browserContext"],
  computed: {
    screenType() {
      switch (this.browserContext.screenType) {
        case 'xs': return 'Mobile';
        case 'sm': return 'Mobile et phablet';
        case 'md': return 'Tablette portrait';
        case 'lg': return 'Tablette paysage';
        case 'xl': return 'Bureau';
      }
    },
    isLargerThanTablet() {
      return this.browserContext.up('md');
    }
  }
}
```
</docs>
